import {iot, mqtt} from 'aws-iot-device-sdk-v2';

export type ConnectionStatus = 'connected' | 'disconnected' | 'connecting' | 'reconnecting';

type SubscriptionCallback = (topic: string, payload: any) => void;

const RETRY_DELAYS = [1000, 2000, 4000];

export class MQTTConnection {
    private connection: mqtt.MqttClientConnection | null = null;

    private subscriptions: Map<string, SubscriptionCallback> = new Map();

    private readonly clientId: string;

    private readonly endpoint: string;

    private email: string = '';

    public token: string;

    private isReconnecting = false;

    private isDestroyed = false;

    private retryTimeout: ReturnType<typeof setTimeout> | null = null;

    private onStatusChange: (status: ConnectionStatus) => void;

    constructor(endpoint: string, token: string, onStatusChange: (status: ConnectionStatus) => void) {
        this.token = token;
        this.endpoint = endpoint;
        this.onStatusChange = onStatusChange;
        this.clientId = `agent-portal-${Date.now()}-${Math.random().toString(16).slice(2)}`;

        console.log('Initializing MQTT with clientId:', this.clientId);
    }

    getConnection() {
        return this.connection;
    }

    private buildConfig(token: string, agentEmail: string) {
        return iot.AwsIotMqttConnectionConfigBuilder.new_with_websockets()
            .with_clean_session(true)
            .with_client_id(this.clientId)
            .with_endpoint(this.endpoint)
            .with_custom_authorizer(
                agentEmail,
                `${process.env.NEXT_PUBLIC_ATHENA_STAGE_NAME}-${process.env.NEXT_PUBLIC_MQTT_AUTHORIZER}`,
                '',
                token,
            )
            .with_keep_alive_seconds(30)
            .build();
    }

    private wireEvents() {
        if (!this.connection) return;

        this.connection.on('disconnect', () => {
            console.log('MQTT Disconnected');
            this.handleUnexpectedDisconnect();
        });

        this.connection.on('error', (error) => {
            console.log('MQTT ERROR:', error);
            this.handleUnexpectedDisconnect();
        });
    }

    private handleUnexpectedDisconnect() {
        if (this.isReconnecting || this.isDestroyed) return;
        console.log('Unexpected disconnect detected, attempting reconnect with latest token');
        this.reconnect(this.token);
    }

    async connect(token: string, agentEmail: string) {
        console.log('Attempting to connect to:', this.endpoint);

        this.token = token;
        this.email = agentEmail;

        const config = this.buildConfig(token, agentEmail);
        const client = new mqtt.MqttClient();
        this.connection = client.new_connection(config);

        this.wireEvents();

        try {
            this.onStatusChange('connecting');
            await this.connection.connect();
            console.log('Connection established successfully');
            this.onStatusChange('connected');
        } catch (err) {
            console.error('Connection failed:', err);
            this.onStatusChange('disconnected');
            throw err;
        }

        return this.connection;
    }

    async reconnect(newToken: string) {
        if (this.isReconnecting || this.isDestroyed) return;

        this.isReconnecting = true;
        this.token = newToken;
        this.onStatusChange('reconnecting');

        console.log('Proactive reconnect starting');

        // Disconnect old connection
        try {
            if (this.connection) {
                // Remove event listeners to prevent recursive disconnect handling
                this.connection.removeAllListeners?.();
                await this.connection.disconnect();
            }
        } catch (err) {
            console.warn('Error disconnecting old connection:', err);
        }
        this.connection = null;

        // Attempt reconnect with retries
        for (let attempt = 0; attempt < RETRY_DELAYS.length; attempt++) {
            if (this.isDestroyed) {
                this.isReconnecting = false;
                return;
            }

            try {
                const config = this.buildConfig(this.token, this.email);
                const client = new mqtt.MqttClient();
                this.connection = client.new_connection(config);

                this.wireEvents();
                await this.connection.connect();
                console.log(`Reconnect succeeded on attempt ${attempt + 1}`);

                // Re-subscribe to all tracked topics — any failure aborts this attempt
                for (const [topic, callback] of this.subscriptions.entries()) {
                    await this.connection.subscribe(topic, mqtt.QoS.AtLeastOnce, (t, p) => {
                        try {
                            const message = new TextDecoder().decode(p);
                            const parsedMessage = JSON.parse(message);
                            callback(t, parsedMessage);
                        } catch (err) {
                            console.log('Error processing message:', err);
                        }
                    });
                    console.log(`Re-subscribed to ${topic}`);
                }

                this.onStatusChange('connected');
                this.isReconnecting = false;
                return;
            } catch (err) {
                console.error(`Reconnect attempt ${attempt + 1} failed:`, err);
                this.connection = null;

                if (attempt < RETRY_DELAYS.length - 1 && !this.isDestroyed) {
                    await this.delay(RETRY_DELAYS[attempt]);
                }
            }
        }

        // All retries exhausted
        console.error('All reconnect attempts failed');
        this.onStatusChange('disconnected');
        this.isReconnecting = false;
    }

    async subscribe(topic: string, callback: SubscriptionCallback) {
        if (!this.connection) {
            throw new Error('Not connected');
        }

        try {
            if (topic) {
                await this.connection.subscribe(topic, mqtt.QoS.AtLeastOnce, (t, p) => {
                    try {
                        const message = new TextDecoder().decode(p);
                        const parsedMessage = JSON.parse(message);
                        callback(t, parsedMessage);
                    } catch (err) {
                        console.log('Error processing message:', err);
                    }
                });
            }
        } catch (err) {
            console.log('Subscription failed:', err);
            throw err;
        }
        this.subscriptions.set(topic, callback);
    }

    async unsubscribe(topic: string) {
        if (!this.subscriptions.has(topic)) {
            console.warn(`No active subscription found for topic: ${topic}`);
            return;
        }

        this.subscriptions.delete(topic);

        if (!this.connection) {
            console.warn('Unsubscribed from tracking without connection');
            return;
        }

        try {
            await this.connection.unsubscribe(topic);
            console.log(`Successfully unsubscribed from ${topic}`);
        } catch (err) {
            console.error(`Error while unsubscribing from ${topic}:`, err);
            throw err;
        }
    }

    async destroy() {
        this.isDestroyed = true;

        if (this.retryTimeout) {
            clearTimeout(this.retryTimeout);
            this.retryTimeout = null;
        }

        if (this.connection) {
            this.connection.removeAllListeners?.();
            console.log('Destroying MQTT connection...');
            try {
                await this.connection.disconnect();
                console.log('Destroyed successfully');
            } catch (err) {
                console.error('Destroy disconnect failed:', err);
            }
        }

        this.connection = null;
    }

    private delay(ms: number): Promise<void> {
        return new Promise((resolve) => {
            this.retryTimeout = setTimeout(() => {
                this.retryTimeout = null;
                resolve();
            }, ms);
        });
    }
}
